import React, { useMemo, useState } from "react";
import { Animated, Platform, View as RNView } from "react-native";
import { View, Text, Button } from "@src/components";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import type { TimelineEntry, SimilarFields } from "@visioni/a3s-vehicle-tracker";
import { TimelineCard } from "./TimelineCard";
import { ClusterCard } from "./ClusterCard";
import { GroupCard } from "./GroupCard";
import { formatGap } from "@visioni/a3s-vehicle-tracker";
import { formatTime, formatDateHeader, getDateKey } from "@visioni/a3s-vehicle-tracker/app";

const RAIL_WIDTH = 2;
const DOT_SIZE = 8;

const GUTTER_BY_BREAKPOINT: Record<string, number> = {
  xs: 0,
  sm: 0,
  md: 56,
  lg: 56,
  xl: 56,
};

const monoFontFamily = Platform.select({ ios: "Menlo", android: "monospace", default: "monospace" });
const noWrap = Platform.OS === "web" ? ({ whiteSpace: "nowrap" } as Record<string, string>) : undefined;

interface TimelineViewProps {
  entries: TimelineEntry[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  activeTimezone: string;
  actions: {
    onFindSimilar: (fields: SimilarFields) => void;
  };
}

interface DaySection {
  dateKey: string;
  headerTime: string;
  data: TimelineEntry[];
  globalOffset: number;
}

function getEntryTime(entry: TimelineEntry): string {
  if (entry.kind === "single") return entry.vehicle.firstSeenAt;
  if (entry.kind === "cluster") return entry.vehicles[0]!.firstSeenAt;
  return entry.group.members.reduce(
    (earliest, m) => (m.firstSeenAt < earliest ? m.firstSeenAt : earliest),
    entry.group.members[0]!.firstSeenAt,
  );
}

function groupIntoSections(entries: TimelineEntry[], activeTimezone: string): DaySection[] {
  const sections: DaySection[] = [];
  let currentSection: DaySection | null = null;
  let globalOffset = 0;

  for (const entry of entries) {
    const time = getEntryTime(entry);
    const key = getDateKey(time, activeTimezone);

    if (!currentSection || currentSection.dateKey !== key) {
      currentSection = { dateKey: key, headerTime: time, data: [], globalOffset };
      sections.push(currentSection);
    }
    currentSection.data.push(entry);
    globalOffset++;
  }

  return sections;
}

export function TimelineView({ entries, loading, hasMore, onLoadMore, activeTimezone, actions }: TimelineViewProps) {
  const { theme } = useUnistyles();
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const toggleExpanded = (id: string) => setExpandedEventId((prev) => (prev === id ? null : id));
  const breakpoint = (UnistylesRuntime.breakpoint as string) ?? "md";
  const gutterWidth = GUTTER_BY_BREAKPOINT[breakpoint] ?? 60;
  const showRail = gutterWidth > 0;

  const sections = useMemo(() => groupIntoSections(entries, activeTimezone), [entries, activeTimezone]);

  // SectionList passes each cell's data as `item` to CellRendererComponent: a TimelineEntry for
  // rows, and the DaySection for its header/footer. Cells share a stacking context, so an expanded
  // card in one row can be clipped by later rows painted on top. We only need to fix the ordering
  // AMONG ROWS: give each row a non-positive, globally descending zIndex (0, -1, -2, …) so an
  // earlier row's expanded/overflowing content paints above the rows that follow it.
  //
  // Headers deliberately get NO explicit zIndex: React Native's sticky-header wrapper already
  // elevates a stuck header (zIndex: 10). Keeping row zIndex <= 0 means (a) a stuck header always
  // stays above scrolled-under rows, and (b) in normal flow a header still paints before its own
  // section's rows, so the timeline rail renders above the header background instead of under the
  // date line.
  const rowZIndex = useMemo(() => {
    const map = new Map<TimelineEntry, number>();
    entries.forEach((entry, index) => map.set(entry, -index));
    return map;
  }, [entries]);

  return (
    <View flex={1}>
      <Animated.SectionList
        sections={sections}
        stickySectionHeadersEnabled
        keyExtractor={(item: TimelineEntry) => {
          if (item.kind === "single") return item.vehicle.id ?? `${item.vehicle.createdAt}-${item.vehicle.plateText}`;
          if (item.kind === "cluster") return `cluster-${item.plate}-${item.vehicles[0]!.firstSeenAt}`;
          return `group-${item.group.key}`;
        }}
        CellRendererComponent={({ children, style, item, ...props }: any) => {
          // Only rows are in rowZIndex; headers return undefined and keep their natural zIndex.
          // `overflow: "visible"` lets an expanded card's content spill past the cell bounds; the
          // negative zIndex (see rowZIndex above) makes that spill paint over LATER rows.
          // Do NOT add a zIndex to headers here — it fights RN's sticky wrapper and makes the
          // header background paint over the timeline rail. See rowZIndex comment.
          const z = rowZIndex.get(item);
          return (
            <RNView {...props} style={[style, z != null ? { zIndex: z } : null, { overflow: "visible" }]}>
              {children}
            </RNView>
          );
        }}
        renderSectionHeader={({ section }: { section: DaySection }) => (
          // Opaque background is required so scrolled-under rows don't show through the stuck
          // header. RN's sticky-header wrapper applies zIndex: 10 on its own when stuck, which is
          // why we must keep row zIndex <= 0 (see rowZIndex) rather than giving the header a zIndex.
          <RNView
            style={{
              backgroundColor: theme.colors.background,
              paddingTop: section.globalOffset === 0 ? 0 : 12,
              paddingBottom: 8,
            }}
          >
            <View flexDirection="row" alignItems="center">
              <Text textStyle="xs/semibold" color="text" style={[{ fontFamily: monoFontFamily }, noWrap]}>
                {formatDateHeader(section.headerTime, activeTimezone)}
              </Text>
              <View flex={1} ml="md" style={{ height: 1, backgroundColor: theme.colors.border }} />
            </View>
          </RNView>
        )}
        renderItem={({ item, index, section }: { item: TimelineEntry; index: number; section: DaySection }) => {
          const entryTime = getEntryTime(item);
          const prevEntry = index > 0 ? section.data[index - 1] : undefined;
          const prevEntryTime = prevEntry ? getEntryTime(prevEntry) : null;
          const gapLabel = prevEntryTime ? formatGap(prevEntryTime, entryTime) : null;
          const isCluster = item.kind === "cluster" || item.kind === "group";
          const isFirstInSection = index === 0;
          const globalIndex = section.globalOffset + index;
          const isFirstGlobal = globalIndex === 0;
          const isLastGlobal = globalIndex === entries.length - 1;

          return (
            <View>
              {gapLabel && (
                <View flexDirection="row" alignItems="stretch" style={{ minHeight: showRail ? 44 : 28 }}>
                  {showRail && (
                    <>
                      <View style={{ width: gutterWidth, flexShrink: 0 }} />
                      <View style={{ width: DOT_SIZE, alignItems: "center", justifyContent: "center" }}>
                        <View
                          style={{
                            width: RAIL_WIDTH,
                            flex: 1,
                            borderLeftWidth: RAIL_WIDTH,
                            borderLeftColor: theme.colors.border,
                            borderStyle: "dashed",
                          }}
                        />
                      </View>
                    </>
                  )}
                  <View ml={showRail ? "md" : undefined} justifyContent="center">
                    <View borderRadius={999} borderWidth={1} borderColor="border" px="sm" py="xs">
                      <Text textStyle="xxs/semibold" color="textMuted" style={{ fontFamily: monoFontFamily }}>
                        {gapLabel}
                      </Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Entry row */}
              <View flexDirection="row" alignItems="stretch">
                {showRail && (
                  <>
                    {/* Time label gutter */}
                    <View
                      style={{ width: gutterWidth, flexShrink: 0 }}
                      alignItems="flex-end"
                      pr="md"
                      justifyContent="flex-start"
                      pt="lg"
                    >
                      <Text textStyle="xxs/regular" color="textMuted" style={[{ fontFamily: monoFontFamily }, noWrap]}>
                        {formatTime(entryTime, activeTimezone)}
                      </Text>
                    </View>

                    {/* Rail line + dot. The three segments below stitch into one continuous
                        vertical rail across all rows: a top stub (skipped on the very first row so
                        the rail doesn't poke above it), the dot, and a flex spacer running down to
                        the next row (skipped on the last row). isFirstInSection uses a taller stub
                        to bridge the gap left by the section header. */}
                    <View style={{ width: DOT_SIZE, alignItems: "center" }}>
                      {!isFirstGlobal && (
                        <View
                          style={{
                            width: RAIL_WIDTH,
                            height: isFirstInSection ? 12 : 8,
                            backgroundColor: theme.colors.border,
                          }}
                        />
                      )}
                      {isFirstGlobal && <View style={{ height: 18 }} />}
                      <View
                        style={{
                          width: DOT_SIZE,
                          height: DOT_SIZE,
                          borderRadius: DOT_SIZE / 2,
                          backgroundColor:
                            isCluster ? theme.colors.interactive.controls.selected_midground : theme.colors.border,
                        }}
                      />
                      {!isLastGlobal && (
                        <View style={{ width: RAIL_WIDTH, flex: 1, backgroundColor: theme.colors.border }} />
                      )}
                    </View>
                  </>
                )}

                {/* Card */}
                <View flex={1} ml={showRail ? "md" : undefined}>
                  {item.kind === "single" ?
                    <TimelineCard
                      vehicle={item.vehicle}
                      expandedEventId={expandedEventId}
                      onToggleExpanded={toggleExpanded}
                      activeTimezone={activeTimezone}
                      actions={actions}
                    />
                  : item.kind === "cluster" ?
                    <ClusterCard
                      cluster={item}
                      expandedEventId={expandedEventId}
                      onToggleExpanded={toggleExpanded}
                      activeTimezone={activeTimezone}
                      actions={actions}
                    />
                  : <GroupCard
                      group={item.group}
                      expandedEventId={expandedEventId}
                      onToggleExpanded={toggleExpanded}
                      activeTimezone={activeTimezone}
                      actions={actions}
                    />
                  }
                </View>
              </View>
            </View>
          );
        }}
        contentContainerStyle={{ paddingBottom: 80 }}
        showsVerticalScrollIndicator
        ListFooterComponent={
          hasMore ?
            <View alignItems="center" mt="lg">
              <Button
                variant="secondary"
                size="md"
                onPress={onLoadMore}
                disabled={loading}
                label={loading ? "Loading..." : "Load more"}
              />
            </View>
          : entries.length > 0 ?
            <View alignItems="center" mt="lg" mb="md">
              <Text textStyle="sm/regular" color="textMuted">
                End of results
              </Text>
            </View>
          : null
        }
      />
    </View>
  );
}
